import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMenu } from '../context/MenuContext';
import MenuShareButton from './MenuShareButton';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUsers, FiClock, FiArrowRight, FiArrowLeft, FiGift, FiCheckCircle } = FiIcons;

const NextWeekMenu = () => {
  const navigate = useNavigate();
  const { nextWeeklyMenus, isWeeklyMenuOrderable, getOrderableDaysForWeeklyMenu } = useMenu();

  // KORRIGIERTE SORTIERUNG: Sortiere die nächste Woche Menüs nach der richtigen Reihenfolge
  const getSortedNextWeekMenus = () => {
    const dayOrder = { 'dienstag': 1, 'mittwoch': 2, 'donnerstag': 3, 'freitag': 4 };
    return Object.entries(nextWeeklyMenus).sort(([dayA], [dayB]) => {
      const orderA = dayOrder[dayA?.toLowerCase()] || 999;
      const orderB = dayOrder[dayB?.toLowerCase()] || 999;
      return orderA - orderB;
    });
  };

  const hasNextWeekMenus = Object.entries(nextWeeklyMenus).length > 0;

  // Prüfen, ob Wochenmenü für nächste Woche bestellbar ist
  const canOrderWeeklyMenu = isWeeklyMenuOrderable(true);

  // Bestellbare Tage für das Wochenmenü der nächsten Woche
  const orderableDays = getOrderableDaysForWeeklyMenu(true);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-lg"
      >
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              Menüs für nächste Woche
            </h1>
            <p className="text-lg text-gray-600">
              Planen Sie Ihre Mahlzeiten für die kommende Woche
            </p>
          </div>
        </div>
      </motion.header>

      {/* Zurück-Button */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold"
        >
          <SafeIcon icon={FiArrowLeft} />
          <span>Zurück zur Übersicht</span>
        </motion.button>
      </div>

      {/* Wochenanmeldung für nächste Woche */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="max-w-6xl mx-auto px-4 py-8"
      >
        {hasNextWeekMenus && canOrderWeeklyMenu && (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white text-center shadow-xl">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-2/3 mb-6 md:mb-0 md:pr-6 text-left">
                <h2 className="text-3xl font-bold mb-4">Für die ganze nächste Woche anmelden</h2>
                <p className="text-xl mb-3">
                  Bestellbare Tage: {orderableDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')}
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 text-white/80" />
                    <span>Sparen Sie Zeit mit nur einer Anmeldung</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 text-white/80" />
                    <span>Erhalten Sie ein kostenloses Dessert</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 text-white/80" />
                    <span>Planen Sie Ihre Woche im Voraus</span>
                  </div>
                </div>
              </div>
              <div className="md:w-1/3 flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/anmeldung/next-woche')}
                  className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2"
                >
                  <span>Wochenanmeldung</span>
                  <SafeIcon icon={FiArrowRight} />
                </motion.button>
              </div>
            </div>
          </div>
        )}
      </motion.section>

      {/* Benefits Banner */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="bg-blue-50 py-8 mb-8"
      >
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-center text-2xl font-bold mb-6 text-blue-800">Ihre Vorteile bei Vorbestellung</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-5 text-center shadow-md">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiGift} className="text-2xl text-blue-600" />
              </div>
              <h3 className="font-bold text-xl mb-2 text-blue-700">Wochenbonus</h3>
              <p className="text-gray-600">Bei Wochenbestellung erhalten Sie ein kostenloses Dessert</p>
            </div>
            <div className="bg-white rounded-xl p-5 text-center shadow-md">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCalendar} className="text-2xl text-blue-600" />
              </div>
              <h3 className="font-bold text-xl mb-2 text-blue-700">Planungssicherheit</h3>
              <p className="text-gray-600">Reservieren Sie Ihre Mahlzeiten im Voraus</p>
            </div>
            <div className="bg-white rounded-xl p-5 text-center shadow-md">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="text-2xl text-blue-600" />
              </div>
              <h3 className="font-bold text-xl mb-2 text-blue-700">Gruppenvorteile</h3>
              <p className="text-gray-600">Ab 4 Personen: Kostenloser Kaffee, ab 6 Personen: Dessert</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Menüs der nächsten Woche - KORREKT SORTIERT */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Unsere Tagesmenüs für nächste Woche
        </h2>

        {!hasNextWeekMenus ? (
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <p className="text-gray-500 mb-4">Noch keine Menüs für nächste Woche verfügbar</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Zurück zur aktuellen Woche</span>
            </motion.button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getSortedNextWeekMenus().map(([key, menu], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative">
                  <img
                    src={menu.image}
                    alt={menu.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-blue-500 text-white px-3 py-1 rounded-full font-semibold">
                    {menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}
                    {menu.formattedDate ? ` - ${menu.formattedDate}` : ''}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {menu.name}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {menu.description}
                  </p>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-primary-600">
                      CHF {menu.price.toFixed(2)}
                    </span>
                    <div className="flex items-center text-gray-500 text-sm">
                      <SafeIcon icon={FiClock} className="mr-1" />
                      <span>12:00 Uhr</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate(`/anmeldung/next-${key}`)}
                      className="w-full bg-blue-500 text-white py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors duration-300 flex items-center justify-center space-x-2"
                    >
                      <span>Vorbestellen</span>
                      <SafeIcon icon={FiArrowRight} />
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate(`/anmeldung/next-flexible/${key}`)}
                      className="w-full bg-green-500 text-white py-2 rounded-xl font-medium hover:bg-green-600 transition-colors duration-300 flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiCalendar} />
                      <span>5 Tage ab hier buchen</span>
                    </motion.button>
                  </div>

                  {/* ✅ NEUE SHARING-BUTTONS */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-sm text-gray-500">Teilen:</span>
                    <MenuShareButton menu={menu} isNextWeek={true} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Zurück zur Übersicht Button */}
      <div className="max-w-6xl mx-auto px-4 py-8 text-center">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors inline-flex items-center space-x-2"
        >
          <SafeIcon icon={FiArrowLeft} />
          <span>Zurück zur Übersicht</span>
        </motion.button>
      </div>
    </div>
  );
};

export default NextWeekMenu;